import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import LazyImage from "@/my_components/Common/LazyImage";

import { useAppContext } from "@/context/Context";
import { getCourses } from "@/services/courseService";
import { ROUTE } from "@/route/app_routes.js";

const RESULT_COUNT = 12;
const DISCOVERY_COUNT = 6;
const DEBOUNCE_MS = 350;

const Search = () => {
  const { search, setSearch } = useAppContext();
  const [query, setQuery] = useState("");
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  // Shown instead of `courses` while there's no search term yet -- two curated rows (highest
  // enrollment, highest rating) so the panel has something worth looking at the instant it
  // opens, not just an empty state waiting on the first keystroke.
  const [trending, setTrending] = useState([]);
  const [favourites, setFavourites] = useState([]);
  const [discoveryLoading, setDiscoveryLoading] = useState(false);
  const debounceRef = useRef(null);
  const inputRef = useRef(null);
  // Guards against an in-flight request for a stale query overwriting the results of a newer
  // one that happened to resolve first (e.g. "php" issued, then "react" right after -- "react"'s
  // response arriving before "php"'s would otherwise leave "react" typed with "php"'s results
  // on screen). Each call stamps its own request, and a response only applies if it's still the
  // latest one issued by the time it resolves.
  const requestIdRef = useRef(0);
  const discoveryRequestIdRef = useRef(0);

  const fetchCourses = (term) => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    getCourses({ page: 1, search: term, per_page: RESULT_COUNT })
      .then((res) => {
        if (requestId !== requestIdRef.current) return;
        setCourses(res.data?.data || []);
      })
      .catch(() => {})
      .finally(() => {
        if (requestId !== requestIdRef.current) return;
        setLoading(false);
      });
  };

  const fetchDiscoveryRows = () => {
    const requestId = ++discoveryRequestIdRef.current;
    setDiscoveryLoading(true);
    Promise.all([
      getCourses({ page: 1, per_page: DISCOVERY_COUNT, sort: "popular" }),
      getCourses({ page: 1, per_page: DISCOVERY_COUNT, sort: "rating" }),
    ])
      .then(([trendingRes, favouritesRes]) => {
        if (requestId !== discoveryRequestIdRef.current) return;
        setTrending(trendingRes.data?.data || []);
        setFavourites(favouritesRes.data?.data || []);
      })
      .catch(() => {})
      .finally(() => {
        if (requestId !== discoveryRequestIdRef.current) return;
        setDiscoveryLoading(false);
      });
  };

  // initial load (no search term) + whenever the dropdown re-opens
  useEffect(() => {
    if (!search) {
      fetchDiscoveryRows();
      inputRef.current?.focus();
    }
  }, [search]);

  useEffect(() => {
    const term = query.trim();
    // Empty query means "show the curated Trending/Learners Favourite rows instead" (handled by
    // the discovery fetch above) -- no need to also hit the plain search endpoint with "".
    if (!term) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchCourses(term);
    }, DEBOUNCE_MS);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  return (
    <>
      <div className={`rbt-search-dropdown ${!search ? "active" : ""}`}>
        <div className="wrapper">
          {/* Mobile-only header: the trigger button up in the navbar swaps to an X icon when
              this is open, but that's easy to miss as "the way to close this" -- especially
              once the panel covers the full screen below (see the mobile-only full-screen CSS).
              An explicit, always-visible Cancel row removes any doubt. */}
          <div className="rbt-search-dropdown__mobile-head">
            <span className="rbt-search-dropdown__mobile-title">Search courses</span>
            <button type="button" className="rbt-search-dropdown__cancel" onClick={() => setSearch(true)}>
              Cancel
            </button>
          </div>

          <div className="row">
            <div className="col-lg-12">
              <form onSubmit={(e) => e.preventDefault()} className="rbt-live-search-form">
                <i className="feather-search rbt-live-search-form__icon" aria-hidden="true"></i>
                <input
                  ref={inputRef}
                  type="text"
                  placeholder="What do you want to learn?"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
                {query && (
                  <button
                    type="button"
                    className="rbt-search-clear-btn"
                    title="Clear search"
                    onClick={() => setQuery("")}
                  >
                    <i className="feather-x"></i>
                  </button>
                )}
              </form>
            </div>
          </div>


          <div className="rbt-separator-mid">
            <hr className="rbt-separator m-0" />
          </div>


          {query ? (
            <div className="row g-4 pt--30 pb--60">
              <div className="col-lg-12">
                <div className="section-title">
                  <h5 className="rbt-title-style-2">{`Results for "${query}"`}</h5>
                </div>
              </div>

              {loading ? (
                <div className="col-lg-12">
                  <p>Searching&hellip;</p>
                </div>
              ) : courses.length === 0 ? (
                <div className="col-lg-12">
                  <p>No courses found.</p>
                </div>
              ) : (
                courses.map((data) => <CourseResultCard key={data.id} data={data} />)
              )}
            </div>
          ) : (
            <>
              <div className="row g-4 pt--30">
                <div className="col-lg-12">
                  <div className="section-title">
                    <h5 className="rbt-title-style-2">Trending Courses</h5>
                  </div>
                </div>

                {discoveryLoading ? (
                  <div className="col-lg-12">
                    <p>Loading&hellip;</p>
                  </div>
                ) : (
                  trending.map((data) => <CourseResultCard key={data.id} data={data} />)
                )}
              </div>

              <div className="row g-4 pt--30 pb--60">
                <div className="col-lg-12">
                  <div className="section-title">
                    <h5 className="rbt-title-style-2">Learners Favourite</h5>
                  </div>
                </div>

                {discoveryLoading ? (
                  <div className="col-lg-12">
                    <p>Loading&hellip;</p>
                  </div>
                ) : (
                  favourites.map((data) => <CourseResultCard key={data.id} data={data} />)
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

// Shared result-row card -- identical for the search-results list and both curated discovery
// rows (Trending Courses / Learners Favourite), just fed a different `data` course object.
const CourseResultCard = ({ data }) => (
  <div className="col-lg-3 col-md-4 col-sm-6 col-12">
    <div className="rbt-card variation-01 rbt-hover">
      <div className="rbt-card-img">
        <Link href={`${ROUTE.course_details}${data.id}`}>
          {/* fill, not a fixed width/height -- 186x128 was sized for the desktop grid's narrow
              col-lg-3 cell specifically. At mobile, where this grid column is a full-width
              col-12, that same fixed size left the image stuck at 186px wide inside an
              ~320px-wide card, with the rest showing as dead white space next to it instead of
              a full-bleed thumbnail. fill (matching the pattern used by every other responsive
              course-card grid on the site) sizes to whatever the card's own CSS gives it at any
              width. */}
          <LazyImage
            src={data.course_image_small}
            alt={data.title}
            fill
            sizes="(max-width: 767px) 92vw, (max-width: 1199px) 30vw, 22vw"
          />
        </Link>
      </div>
      <div className="rbt-card-body">
        <h5 className="rbt-card-title">
          <Link href={`${ROUTE.course_details}${data.id}`}>{data.title}</Link>
        </h5>
        <div className="rbt-card-bottom">
          <span className="rbt-badge-5 bg-color-success-opacity color-success">Free</span>
        </div>
      </div>
    </div>
  </div>
);

export default Search;
